import { useMemo, useState } from 'react';
import type {
  PresenceContext,
  TesterMenuGroup,
  TesterPresenceItem,
  TesterPresenceTimingConfig,
} from '@runhuman/shared';
import { groupTestersByMenuGroup, MENU_GROUP_ORDER, webRoutes } from '@runhuman/shared';
import { PresenceSection } from './PresenceSection';
import { WebPopoutButton } from '../WebPopoutButton';

interface Props {
  readonly testers: readonly TesterPresenceItem[];
  /**
   * Server-driven presence thresholds. When undefined (initial fetch
   * pending), the roster suspends rendering rather than guessing values
   * out from under the bucketing — same approach the web Tester Portal
   * takes against its `useInactivityConfig` hook.
   */
  readonly timing: TesterPresenceTimingConfig | undefined;
  /**
   * When set, individual tester rows become clickable; the callback fires
   * with the row's `tester`. Threaded down from `<TesterView>` and only
   * provided for admin viewers (#3967).
   */
  readonly onTesterClick?: (tester: TesterPresenceItem) => void;
  /**
   * When true, renders an "Open testers in admin" affordance next to the
   * section heading (#4105). Set by `<TesterView>` when the viewer is admin
   * — only admins have meaningful access to `/admin/testers`.
   */
  readonly showAdminLink?: boolean;
}

/**
 * Global active-tester roster — same data the web Tester Portal renders in
 * its presence sidebar (`<PresenceMenu>`), bucketed into the canonical six
 * groups (`online` / `busy` / `away` / `missing` / `offline` / `inactive`)
 * via the shared `getMenuGroup()` derivation.
 *
 * Pre-#3838 this component was a single flat `<ul>` keyed off the stored
 * `availability` field and rendered no status dots, no avatar fallbacks,
 * and no derived buckets — making testers actively working a job
 * indistinguishable from idle ones (the reform migration in #3280 made
 * `busy` a derivation, not a stored value, and the iframe never caught
 * up). This rewrite sources its grouping from `@runhuman/shared` so the
 * Activity, web, and mobile all answer "who is busy right now?" the same
 * way against the same `TesterPresenceItem` shape.
 *
 * Server-driven `timing` thresholds keep the Activity in lockstep with
 * the dashboard — preview environments tune `inactiveDays` etc. via env
 * vars and the Activity picks those up via `/discord/activity/config`.
 */
/**
 * Groups that ship collapsed by default (#3920). The actionable groups
 * (`online` / `busy` / `away` / `missing`) stay expanded so the at-a-glance
 * roster is dense with the testers you might actually engage with right now,
 * and the "who's been around lately" tail (`offline` / `inactive`) is one
 * click away when you actually need it.
 */
const DEFAULT_COLLAPSED_GROUPS: ReadonlySet<TesterMenuGroup> = new Set([
  'offline',
  'inactive',
]);

export function PresenceRoster({ testers, timing, onTesterClick, showAdminLink }: Props) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<TesterMenuGroup>>(
    () => new Set(DEFAULT_COLLAPSED_GROUPS),
  );

  const groups = useMemo(() => {
    if (!timing) return null;
    const ctx: PresenceContext = {
      nowMs: Date.now(),
      inactiveThresholdDays: timing.inactiveDays,
      // Web hardcodes the missing-window at 60 minutes (#3280) — the value
      // is not server-driven there either, so we mirror it exactly to keep
      // bucketing parity with `/admin/home` and `/tester`.
      missingThresholdMinutes: 60,
    };
    return groupTestersByMenuGroup(testers, ctx);
  }, [testers, timing]);

  if (!timing || !groups) {
    return (
      <section className="presence presence--loading" aria-live="polite">
        <PresenceHeading label="Active testers" showAdminLink={showAdminLink} />
        <p className="presence__hint">Loading…</p>
      </section>
    );
  }

  if (testers.length === 0) {
    return (
      <section className="presence presence--empty">
        <PresenceHeading label="Active testers (0)" showAdminLink={showAdminLink} />
        <p className="presence__hint">No active testers right now.</p>
      </section>
    );
  }

  const toggle = (group: TesterMenuGroup) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  return (
    <section className="presence">
      <PresenceHeading label={`Active testers (${testers.length})`} showAdminLink={showAdminLink} />
      <div className="presence__sections">
        {MENU_GROUP_ORDER.map((group) => {
          const members = groups[group];
          if (members.length === 0) return null;
          return (
            <PresenceSection
              key={group}
              group={group}
              testers={members}
              collapsed={collapsed.has(group)}
              onToggle={toggle}
              onTesterClick={onTesterClick}
            />
          );
        })}
      </div>
    </section>
  );
}

/**
 * Heading row owns the "Active testers (N)" label and the optional
 * `<WebPopoutButton>` admin handoff (#4105). Extracted from the three render
 * branches above so loading / empty / populated states share one heading
 * shape — the admin link doesn't disappear just because the roster happens
 * to be empty right now.
 */
function PresenceHeading({
  label,
  showAdminLink,
}: {
  readonly label: string;
  readonly showAdminLink: boolean | undefined;
}) {
  return (
    <div className="presence__heading">
      <h3 className="presence__title">{label}</h3>
      {showAdminLink ? (
        <WebPopoutButton
          path={webRoutes.adminTesters.build()}
          className="presence__admin-link"
        >
          Open in admin
        </WebPopoutButton>
      ) : null}
    </div>
  );
}
